<template>
  <v-container class="gallery-filters">
    <div class="gallery-filters-row">
      <div
        v-for="filter in filters"
        :key="filter.key"
        class="gallery-filter"
        :ref="(element) => setFilterElement(filter.key, element)"
        :style="filterStyle(filter.key)"
      >
        <v-select
          class="gallery-filter-select"
          :label="filter.label"
          :items="filter.items"
          :model-value="filter.value"
          :menu-props="menuProps"
          @update:model-value="filter.set"
        ></v-select>
        <!-- Hidden copy of every option. Measuring these is the only way to know
             how wide the widest one is: the field only ever renders the current
             selection, so its own content says nothing about the rest. The spans
             reuse the selection's own class so they are measured with the font
             and spacing the selected text really uses. -->
        <span class="gallery-filter-probe" aria-hidden="true">
          <span
            v-for="item in filter.items"
            :key="item.value"
            class="v-select__selection-text"
            >{{ item.title }}</span
          >
        </span>
      </div>
      <!-- Only worth offering while something is filtered. The selection lives in
           the store, which is shared, so a leftover value would keep filtering
           whatever gallery is opened next. -->
      <v-btn
        v-if="hasActiveFilters"
        class="gallery-filters-clear"
        variant="text"
        aria-label="Clear filters"
        title="Clear filters"
        @click="clearFilters"
      >
        <!-- Undo arrow inside a circle, filled rather than stroked. The fills are
             left to currentColor so the arrow follows the button colour. -->
        <svg
          class="gallery-filters-clear-icon"
          viewBox="0 0 24 24"
          focusable="false"
          aria-hidden="true"
        >
          <path
            d="M12 20.75C10.078 20.7474 8.23546 19.9827 6.8764 18.6236C5.51733 17.2645 4.75265 15.422 4.75 13.5C4.75 13.3011 4.82902 13.1103 4.96967 12.9697C5.11032 12.829 5.30109 12.75 5.5 12.75C5.69891 12.75 5.88968 12.829 6.03033 12.9697C6.17098 13.1103 6.25 13.3011 6.25 13.5C6.25 14.6372 6.58723 15.7489 7.21905 16.6945C7.85087 17.6401 8.74889 18.3771 9.79957 18.8123C10.8502 19.2475 12.0064 19.3614 13.1218 19.1395C14.2372 18.9177 15.2617 18.37 16.0659 17.5659C16.87 16.7617 17.4177 15.7372 17.6395 14.6218C17.8614 13.5064 17.7475 12.3502 17.3123 11.2996C16.8771 10.2489 16.1401 9.35087 15.1945 8.71905C14.2489 8.08723 13.1372 7.75 12 7.75H9.5C9.30109 7.75 9.11032 7.67098 8.96967 7.53033C8.82902 7.38968 8.75 7.19891 8.75 7C8.75 6.80109 8.82902 6.61032 8.96967 6.46967C9.11032 6.32902 9.30109 6.25 9.5 6.25H12C13.9228 6.25 15.7669 7.01384 17.1265 8.37348C18.4862 9.73311 19.25 11.5772 19.25 13.5C19.25 15.4228 18.4862 17.2669 17.1265 18.6265C15.7669 19.9862 13.9228 20.75 12 20.75Z"
          />
          <path
            d="M12 10.75C11.9015 10.7505 11.8038 10.7313 11.7128 10.6935C11.6218 10.6557 11.5393 10.6001 11.47 10.53L8.47001 7.53003C8.32956 7.38941 8.25067 7.19878 8.25067 7.00003C8.25067 6.80128 8.32956 6.61066 8.47001 6.47003L11.47 3.47003C11.5387 3.39634 11.6215 3.33724 11.7135 3.29625C11.8055 3.25526 11.9048 3.23322 12.0055 3.23144C12.1062 3.22966 12.2062 3.24819 12.2996 3.28591C12.393 3.32363 12.4778 3.37977 12.549 3.45099C12.6203 3.52221 12.6764 3.60705 12.7141 3.70043C12.7519 3.79382 12.7704 3.89385 12.7686 3.99455C12.7668 4.09526 12.7448 4.19457 12.7038 4.28657C12.6628 4.37857 12.6037 4.46137 12.53 4.53003L10.06 7.00003L12.53 9.47003C12.6705 9.61066 12.7494 9.80128 12.7494 10C12.7494 10.1988 12.6705 10.3894 12.53 10.53C12.4608 10.6001 12.3782 10.6557 12.2872 10.6935C12.1962 10.7313 12.0986 10.7505 12 10.75Z"
          />
        </svg>
      </v-btn>
    </div>
  </v-container>
</template>

<script>
import { computed, onMounted, reactive, watch } from "vue";
import { useStore } from "vuex";
import {
  activeImageFilter,
  facetImages,
  filterKeys,
  imageFilterItems,
  imageFilters,
  tagFilters,
} from "@/assets/filters";

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

export default {
  name: "GalleryFilters",
  props: {
    images: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    const store = useStore();
    const filterElements = reactive({});
    const widths = reactive({});

    // The menu is teleported to the body, so the scoped styles below cannot reach
    // it. This class is the hook the global stylesheet uses to paint it
    const menuProps = { contentClass: "gallery-filter-menu" };

    // Every filter offers the values left after the others are applied, so a
    // dropdown never suggests a combination that would render nothing. A filter
    // is left out of its own set, so its options do not depend on its selection
    const facetImagesFor = (key) =>
      facetImages(props.images, store.state.filters, key);

    const facetTagsFor = (key) =>
      new Set(facetImagesFor(key).flatMap((image) => image.tags ?? []));

    // One dropdown per value read off the image itself: year, artist, ...
    const imageSelects = computed(() =>
      imageFilters.map((filter) => ({
        key: filter.key,
        label: filter.label,
        items: [
          { title: filter.allLabel ?? "All", value: null },
          ...imageFilterItems(
            facetImagesFor(filter.key),
            filter,
            store.state.filters[filter.key]
          ),
        ],
        // The store can hold a selection this gallery cannot satisfy, so fall
        // back to "all" to stay in sync with what Gallery.vue renders
        value: activeImageFilter(
          props.images,
          filter,
          store.state.filters[filter.key]
        ),
        set: (value) =>
          store.commit("setFilter", { key: filter.key, value }),
      }))
    );

    // One select per facet declared in assets/filters.js, offering only the tags
    // that survive the rest of the selection. Facets with no tags left are left
    // out.
    const tagSelects = computed(() =>
      tagFilters
        .map((facet) => {
          const tags = facetTagsFor(facet.key);
          return {
            key: facet.key,
            label: facet.label,
            items: [
              { title: facet.allLabel ?? "All", value: null },
              ...facet.tags
                .filter((tag) => tags.has(tag))
                .map((tag) => ({ title: capitalize(tag), value: tag })),
            ],
            value: tags.has(store.state.filters[facet.key])
              ? store.state.filters[facet.key]
              : null,
            set: (value) =>
              store.commit("setFilter", { key: facet.key, value }),
          };
        })
        .filter((select) => select.items.length > 1)
    );

    // Both kinds share one shape so the template stays a plain v-for
    const filters = computed(() => [...imageSelects.value, ...tagSelects.value]);

    // Every key, not just the ones this gallery shows: a selection the gallery
    // cannot satisfy is still in the store, and clearing has to clear it too
    const hasActiveFilters = computed(() =>
      filterKeys.some((key) => store.state.filters[key] !== null)
    );

    const clearFilters = () => store.commit("resetFilters");

    function setFilterElement(key, element) {
      if (element) filterElements[key] = element;
      else delete filterElements[key];
    }

    const px = (value) => parseFloat(value) || 0;

    // Widest option plus the field's own chrome, read from the rendered DOM so we
    // do not have to hardcode Vuetify's paddings and icon size
    function measure() {
      if (typeof document === "undefined") return;

      for (const filter of filters.value) {
        const element = filterElements[filter.key];
        if (!element) continue;

        // The select only ever renders the current selection, so the widest
        // option has to be measured from the hidden copy of all of them
        const probe = element.querySelector(".gallery-filter-probe");
        const widestOption = probe
          ? Math.max(
              0,
              ...Array.from(
                probe.children,
                (option) => option.getBoundingClientRect().width
              )
            )
          : 0;

        // Vuetify ellipsizes the selected text, and scrollWidth reports how wide
        // it would need to be. It is the width the visitor actually sees, so it
        // wins over the prediction made from the options
        const selection = element.querySelector(
          ".v-field .v-select__selection-text"
        );
        const needed = Math.max(widestOption, px(selection?.scrollWidth));
        if (!needed) continue;

        const field = element.querySelector(".v-field");
        const input = element.querySelector(".v-field__input");
        if (!field || !input) continue;

        const fieldStyle = window.getComputedStyle(field);
        const inputStyle = window.getComputedStyle(input);
        const append = element.querySelector(".v-field__append-inner");

        // Everything the field spends around the text: its own padding, the
        // text padding, the gap between flex items and the menu icon
        const estimatedChrome =
          px(fieldStyle.paddingLeft) +
          px(fieldStyle.paddingRight) +
          px(inputStyle.paddingLeft) +
          px(inputStyle.paddingRight) +
          px(inputStyle.columnGap) +
          (append ? append.getBoundingClientRect().width : 0);

        // The text gets one extra pixel for every extra pixel of field, so the
        // chrome the field really spends is exactly what is missing from the
        // width already applied. That corrects any padding or gap the estimate
        // missed, and unlike an accumulated correction it cannot run away.
        const room =
          input.clientWidth -
          px(inputStyle.paddingLeft) -
          px(inputStyle.paddingRight);
        const applied = widths[filter.key];
        const rendered = element.getBoundingClientRect().width;
        const actualChrome =
          applied != null && room > 0 && rendered >= applied - 1
            ? applied - room
            : 0;

        widths[filter.key] = Math.ceil(
          needed + Math.max(estimatedChrome, actualChrome)
        );
      }
    }

    // The field would otherwise be as wide as its container allows and ellipsize
    // the selection, so the measured width is set on the wrapper
    function filterStyle(key) {
      return widths[key] ? { width: `${widths[key]}px` } : undefined;
    }

    onMounted(() => {
      measure();
      // The site font swaps in after first paint, which changes every width
      if (document.fonts?.ready) document.fonts.ready.then(measure);
    });

    // flush "post" so the DOM already holds the new labels and the new selection
    // when measured
    watch(
      () =>
        filters.value
          .map((filter) =>
            [
              filter.key,
              filter.value ?? "",
              ...filter.items.map((item) => item.title),
            ].join("|")
          )
          .join("::"),
      measure,
      { flush: "post" }
    );

    return {
      filters,
      filterStyle,
      setFilterElement,
      menuProps,
      hasActiveFilters,
      clearFilters,
    };
  },
};
</script>

<style lang="less" scoped>
@gold: #bfa181;
@turquoise: #e8dcc8;

.gallery-filters {
  padding-top: 1.5rem;
  padding-bottom: 0;
}

.gallery-filters-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
}

.gallery-filter {
  position: relative;
  // Never shrink: the row wraps instead, so a dropdown always keeps the width
  // measured for its widest option
  flex: 0 0 auto;
  max-width: 100%;
}

// Mirrors .v-list-item-title so the measured width is the width the option will
// have inside the open menu
.gallery-filter-probe {
  position: absolute;
  visibility: hidden;
  white-space: nowrap;
  // Every option sits on one long line, so the probe is much wider than the
  // dropdown. Clipping it keeps that from adding a horizontal scrollbar to the
  // page, and the options are still measurable while clipped.
  overflow: hidden;
  width: 0;
  height: 0;
}

.gallery-filter-probe > span {
  // shrink-to-fit so the box is the text width and nothing else
  display: inline-block;
}

// Sits in the row next to the fields and takes their exact box, so the arrow
// reads at the size of the dropdown it clears and lines up with the row
.gallery-filters-clear {
  height: 56px;
  width: 56px;
  min-width: 56px;
  padding: 0;
  color: @gold;

  &:hover {
    // Never a selected item itself, so it takes the selected colour on hover
    color: @turquoise;
  }

  // The state layer Vuetify paints over the button would draw a rectangle behind
  // the arrow. The hover recolours it instead. It lives inside the button, so it
  // never gets this component's scope attribute and needs :deep()
  :deep(.v-btn__overlay) {
    display: none;
  }
}

.gallery-filters-clear-icon {
  width: 56px;
  height: 56px;
  // Filled, so the fill is what carries the button colour
  fill: currentColor;
}

@media (max-width: 600px) {
  .gallery-filter {
    // flex-basis wins over the inline width, so the selects go full width
    flex: 1 1 100%;
  }

  .gallery-filters-clear {
    flex: 1 1 100%;
  }
}
</style>
